import { Badge } from 'src/components/ui/Badge';
import { PAID_STAGES, STAGE_LABEL, STAGE_PRODUCES } from 'src/lib/pipelineStages';
import type { PipelineStage } from 'src/types/editorial';
import { PIPELINE_STAGES } from 'src/types/editorial';

type StagePickerProps = {
  /** The stage the run will stop at. Everything before it runs too — they are its inputs. */
  value: PipelineStage;
  onChange: (stage: PipelineStage) => void;
  /** Stages this broadcast already produced. They are shown as done, not offered as work. */
  done?: ReadonlySet<PipelineStage>;
  disabled?: boolean;
  /** Compact chip layout for the rerun panel, per the stream-detail mockup: numbered pills that
      highlight everything up to the picked stop point, instead of the full descriptive list. */
  chips?: boolean;
};

/**
 * Where to stop, not which boxes to tick.
 *
 * The stages are a dependency chain — the drafter reads what the prenota produced — so a free
 * multi-select would let an editor describe a run that cannot happen and then have it silently
 * corrected. Picking the stop point says the same thing and cannot be wrong.
 *
 * Each option states what it produces and whether it spends money outside the box, because
 * "process the next section" is a spending decision as much as an editorial one.
 */
export function StagePicker({ value, onChange, done, disabled = false, chips = false }: StagePickerProps) {
  if (chips) {
    const stopIndex = PIPELINE_STAGES.indexOf(value);
    return (
      <fieldset className="rerun-steps">
        <legend className="visually-hidden">Elegir hasta qué etapa reprocesar</legend>
        {PIPELINE_STAGES.map((stage, index) => (
          <label key={stage} className={index <= stopIndex ? 'step will-run' : 'step'}>
            <input
              type="radio"
              name="rerun-stage"
              value={stage}
              checked={value === stage}
              disabled={disabled}
              onChange={() => onChange(stage)}
            />
            <span className="step-chip">
              <span className="step-num" aria-hidden="true">
                {index + 1}
              </span>
              {STAGE_LABEL[stage]}
              {PAID_STAGES.has(stage) && (
                <span className="mini-badge mini-badge--cost" title="Llama a servicios de pago fuera del servidor">
                  costo externo
                </span>
              )}
            </span>
          </label>
        ))}
      </fieldset>
    );
  }

  return (
    <ol className="stages">
      {PIPELINE_STAGES.map(stage => {
        const isDone = done?.has(stage) ?? false;
        const included = PIPELINE_STAGES.indexOf(stage) <= PIPELINE_STAGES.indexOf(value);

        return (
          <li key={stage} className="stage" data-included={included} data-done={isDone}>
            <label className="stage__label">
              <input
                type="radio"
                name="stop-after"
                value={stage}
                checked={value === stage}
                disabled={disabled}
                onChange={() => onChange(stage)}
              />
              <span className="stage__name">{STAGE_LABEL[stage]}</span>
              {PAID_STAGES.has(stage) && (
                <Badge tone="info" title="Llama a servicios de pago fuera del servidor">
                  costo externo
                </Badge>
              )}
              {isDone && (
                <Badge tone="supported" title="Ya existe para esta transmisión; volver a correrlo lo reemplaza">
                  hecho
                </Badge>
              )}
            </label>
            <p className="stage__produces">{STAGE_PRODUCES[stage]}</p>
          </li>
        );
      })}
    </ol>
  );
}
